import { morph } from '@theme/morph';

/**
 * Custom Variant Products - color swatches that link to sibling products
 * (each color is its own product, connected via the custom.variant_products
 * metafield - see blocks/custom-variant-products.liquid). Native Shopify
 * combined listings swap in place via variant-picker.js; this reproduces the
 * same in-place swap for this homemade equivalent, instead of a full page
 * reload to the other product's URL.
 */
class CustomVariantProducts extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#abortController?.abort();
  }

  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a.cvp__link');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.classList.contains('cvp__link--current')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    this.#swapTo(link.href);
  };

  /**
   * @param {string} url
   */
  async #swapTo(url) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      const text = await response.text();
      const newDocument = new DOMParser().parseFromString(text, 'text/html');

      const main = document.querySelector('main');
      const newMain = newDocument.querySelector('main');

      if (!main || !newMain) {
        window.location.href = url;
        return;
      }

      morph(main, newMain);
      document.title = newDocument.title;
      history.pushState({}, '', url);
      window.scrollTo({ top: 0, behavior: 'instant' });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      // If anything goes wrong, fall back to a normal navigation rather than
      // silently doing nothing.
      window.location.href = url;
    }
  }
}

if (!customElements.get('custom-variant-products')) {
  customElements.define('custom-variant-products', CustomVariantProducts);
}
